import fetchWithAuth from "@/api/secureFetch";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export type ProductAssignmentMode = "all" | "selected";

export interface ProductAssignmentTarget {
    id: number;
    name: string;
}

export interface ContextMeshAssignmentTarget {
    id: string;
    name: string;
    accessRole: "owner" | "editor" | "viewer";
}

export interface FileAssignmentTargets {
    products: ProductAssignmentTarget[];
    contextMeshes: ContextMeshAssignmentTarget[];
    canAssignAllProducts: boolean;
}

export interface ProductAssignmentItem extends ProductAssignmentTarget {}

export interface ContextMeshAssignmentItem extends ContextMeshAssignmentTarget {
    sourceBindingId: string;
}

export interface FileAssignments {
    products: {
        mode: ProductAssignmentMode;
        ids: number[];
        items: ProductAssignmentItem[];
    };
    contextMeshes: {
        ids: string[];
        items: ContextMeshAssignmentItem[];
    };
}

export interface FileAssignmentPatch {
    products?: {
        mode: ProductAssignmentMode;
        ids: number[];
    };
    contextMeshes?: {
        ids: string[];
    };
}

export interface FileAssignmentRefreshResult {
    delayed: boolean;
    message: string | null;
    requestId: string | null;
}

export interface FileAssignmentUpdateResult {
    assignment: FileAssignments | null;
    refresh: FileAssignmentRefreshResult;
}

type ErrorPayload = {
    error?: string | { message?: string };
    message?: string;
};

async function readJson<T>(response: Response, fallback: string): Promise<T> {
    const payload = await response.json().catch(() => ({})) as ErrorPayload;
    if (!response.ok) {
        const message =
            typeof payload.error === "string"
                ? payload.error
                : payload.error?.message || payload.message || fallback;
        throw new Error(message);
    }
    return payload as T;
}

function emptyAssignments(): FileAssignments {
    return {
        products: { mode: "selected", ids: [], items: [] },
        contextMeshes: { ids: [], items: [] }
    };
}

function normalizeAssignments(value: Partial<FileAssignments> | null | undefined): FileAssignments {
    const fallback = emptyAssignments();
    const productIds = value?.products?.ids ?? [];
    const contextMeshIds = value?.contextMeshes?.ids ?? [];
    return {
        products: {
            mode: value?.products?.mode === "all" ? "all" : "selected",
            ids: productIds,
            items: value?.products?.items ?? []
        },
        contextMeshes: {
            ids: contextMeshIds,
            items: value?.contextMeshes?.items ?? []
        }
    };
}

/**
 * Thin adapter for the target-aware Files APIs. Keeping transport and response
 * normalization here prevents the shared Files UI from depending on route
 * details while the legacy file-scope endpoints remain available elsewhere.
 */
export async function listFileAssignmentTargets(token: string): Promise<FileAssignmentTargets> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/assignment-targets?surface=files`, token);
    const payload = await readJson<Partial<FileAssignmentTargets>>(
        response,
        "Unable to load file assignment targets."
    );
    return {
        products: payload.products ?? [],
        contextMeshes: payload.contextMeshes ?? [],
        canAssignAllProducts: payload.canAssignAllProducts === true
    };
}

export async function queryFileAssignments(
    token: string,
    fileIds: number[]
): Promise<Record<number, FileAssignments>> {
    if (fileIds.length === 0) return {};
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/files/assignments/query`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileIds })
    });
    const payload = await readJson<{ assignments?: Record<string, Partial<FileAssignments>> }>(
        response,
        "Unable to load file assignments."
    );
    return Object.fromEntries(
        Object.entries(payload.assignments ?? {}).map(([fileId, assignment]) => [
            Number(fileId),
            normalizeAssignments(assignment)
        ])
    );
}

export async function updateFileAssignments(
    token: string,
    fileId: number,
    patch: FileAssignmentPatch
): Promise<FileAssignmentUpdateResult> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/files/${fileId}/assignments`, token, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
    });
    const payload = await readJson<{
        assignment?: Partial<FileAssignments>;
        assignments?: Partial<FileAssignments>;
        warning?: string;
        refresh?: {
            status?: string;
            scheduled?: boolean;
            message?: string;
            requestId?: string;
        };
        contextMeshRefresh?: {
            status?: string;
            scheduled?: boolean;
            message?: string;
            requestId?: string;
        };
    }>(response, "Unable to save file assignments.");

    const refresh = payload.refresh ?? payload.contextMeshRefresh;
    const status = refresh?.status?.toLowerCase();
    const delayed = Boolean(
        payload.warning ||
        refresh?.scheduled === false ||
        status === "delayed" ||
        status === "failed" ||
        status === "unavailable"
    );
    return {
        assignment: payload.assignment || payload.assignments
            ? normalizeAssignments(payload.assignment ?? payload.assignments)
            : null,
        refresh: {
            delayed,
            message: payload.warning ?? refresh?.message ?? (delayed ? "Assignments were saved, but score refresh is delayed." : null),
            requestId: refresh?.requestId ?? null
        }
    };
}
